import PackagingTable from './PackagingTable';
import {Query} from 'react-apollo';
import {client} from '@core/GraphQLProvider';
import ScrollToTop from '@components/common/ScrollToTop';
import {CatalogItemDocumentation} from '@services/ProductCatalogService/query.graphql';
import {Processing, Show} from '@ultradent/components';
import partition from 'ramda/src/partition';
import propEq from 'ramda/src/propEq';
import {notEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import {useParams} from 'react-router-dom';

const i18nText = upi.i18n.getTextFromKey;
const formatSku = id => /-/.test( id ) ? id : id + '-';

const groupIngredients = ( data ) => {
    const [active, inactive] = partition( propEq( 'group', 'ACTIVE' ), data || [] );

    return {
        active,
        inactive
    };
}

const labels = {
    ifu: i18nText( 'lbl-ifu', 'IFU (Product Instructions)' ),
    sds: i18nText( 'lbl-sds', 'SDS (Safety Data Sheets)' ),
    allergens: i18nText( 'lbl-allergens', 'Allergens' ),
    ingredients: i18nText( 'lbl-ingredients', 'Ingredients' ),
    activeIngredients: i18nText( 'lbl-activeIngredients', 'Active Ingredients' ),
    inactiveIngredients: i18nText( 'lbl-inactiveIngredients', 'Inactive Ingredients' ),
    productMaterial: i18nText( 'lbl-productMaterialData', 'Product Material Data' ),
    productSymbolsGlossary: i18nText( 'lbl-productSymbolsGlossary', 'Product Symbols Glossary' ),
    productSymbolsGlossaryLink: i18nText( 'lbl-productSymbolsGlossaryLink', 'Product Symbols Glossary — Multilingual' ),
    declarationsOfConformity: i18nText( 'lbl-declarationsOfConformity', 'Declarations of Conformity' ),
    declarationsOfConformityLink: i18nText( 'lbl-declarationsOfConformityLink', 'Declarations of Conformity' ),
    loadingProductDetails: i18nText( 'lbl-loadingProductDetails', 'Loading product details' )
};

export const ProductDocumentationDetail = ( {
                                                kitName,
                                                sku,
                                                barcode,
                                                totalPackagingWeight,
                                                allergens,
                                                ifus,
                                                ingredients,
                                                packagingMaterialDataList,
                                                sds
                                            } ) => {
    const ingredientGroups = groupIngredients( ingredients );

    return (<div data-testid="product-documentation-detail">
            <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                <h2 className="headline-sm mb-2">{kitName}</h2>
                <p className="text-body mb-2">Item# {sku}</p>
                <Show when={notEmptyOrNil( barcode )}>
                    <p className="text-body">GTIN: {barcode}</p>
                </Show>
            </div>
            <Show when={allergens?.length > 0}>
                <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                    <h3 className="headline-xs mb-2">{labels.allergens}</h3>
                    <ul className="bullets ml-4">
                        {allergens?.map( ( item, index ) => <li className="mb-2" key={index}>{item}</li> )}
                    </ul>
                </div>
            </Show>
            <Show when={ifus?.length > 0}>
                <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                    <h3 className="headline-xs mb-2">{labels.ifu}</h3>
                    <div className="ml-4 my-4">
                        {ifus?.map( ( item, index ) => <a className="link-3 block mb-2"
                                                          href={item.document.source}
                                                          title={item.document.description}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          key={index}>{item.title}</a> )}
                    </div>
                </div>
            </Show>
            <Show when={ingredients?.length > 0}>
                <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                    <h3 className="headline-xs mb-2">{labels.ingredients}</h3>
                    <div className="ml-4 my-4">
                        <Show when={ingredientGroups.active.length > 0}>
                            <p className="mb-4">
                                <strong>{labels.activeIngredients}:</strong> {ingredientGroups.active.map( ( {name} ) => name )
                                .join( ', ' )}
                            </p>
                        </Show>
                        <Show when={ingredientGroups.inactive.length > 0}>
                            <p>
                                <strong>{labels.inactiveIngredients}:</strong> {ingredientGroups.inactive.map( ( {name} ) => name )
                                .join( ', ' )}
                            </p>
                        </Show>
                    </div>
                </div>
            </Show>
            <Show when={packagingMaterialDataList?.length > 0}>
                <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                    <h3 className="headline-xs mb-2">{labels.productMaterial}</h3>

                    <div className="ml-4 my-4">
                        <PackagingTable packaging={packagingMaterialDataList}/>
                    </div>
                </div>
            </Show>

            <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                <h3 className="headline-xs mb-2">{labels.productSymbolsGlossary}</h3>
                <div className="ml-4 my-4">
                    <a className="link-3 mb-2 block"
                       href="/resources/product-symbols-glossary">{labels.productSymbolsGlossaryLink}</a>
                </div>
            </div>

            <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                <h3 className="headline-xs mb-2">{labels.declarationsOfConformity}</h3>
                <div className="ml-4 my-4">
                    <a className="link-3 mb-2 block"
                       href="/resources/declarations-of-conformity">{labels.declarationsOfConformityLink}</a>
                </div>
            </div>

            <Show when={sds?.length > 0}>
                <div className="border-b border-grey-lighter border-solid pb-6 mb-6">
                    <h3 className="headline-xs mb-2">{labels.sds}</h3>
                    <div className="ml-4 my-4">
                        {sds?.map( ( item, index ) => <a className="link-3 mb-2 block"
                                                         href={item.document.source}
                                                         title={item.document.description}
                                                         target="_blank"
                                                         rel="noopener noreferrer"
                                                         key={index}>
                                {item.title} &mdash; {item.language}
                            </a> )}
                    </div>
                </div>
            </Show>
        </div>)
};

const _Query = () => {
    const params = useParams();

    return (
        <ScrollToTop>
            <Query
                client={client}
                fetchPolicy="no-cache"
                query={CatalogItemDocumentation}
                variables={{skuValues: formatSku( params.sku )}}>
                {( {data, error, loading} ) => {
                    if ( error ) {
                        return <div>error</div>;
                    }
                    if ( loading ) {

                        return <div className="flex justify-center m-8">
                            <Processing>{labels.loadingProductDetails}</Processing>
                        </div>;
                    }
                    const {product} = data;
                    return <ProductDocumentationDetail {...product}/>
                }}
            </Query>
        </ScrollToTop>
    );
}

export default _Query;
